import React, { useState, useCallback, useMemo } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";
import { ProjectMessageFile } from "../../app/services/projectMessages";
import {
  FolderNode,
  getTotalFileCount,
  flattenFolderToPaths,
} from "./folderUtils";
import { getFileIconFromCategoryOrExtension } from "../file-icon/fileIcon";
import MediaPreviewModal from "../media-preview/components/MediaPreviewModal";
import MediaDetailModal from "../media-preview/components/MediaDetailModal";
import UserAvatar from "../avatar/UserAvatar";
import {
  downloadFile,
  resolveDownloadUrl,
  DOWNLOAD_FAILED_MESSAGE,
} from "../../utils/downloadFile";
import { buildAudioMenuItems } from "../../utils/audioMenu";
import MediaTranscriptionModal from "../media-preview/components/MediaTranscriptionModal";
import BulkTranscriptionModal, {
  BulkTranscriptionTarget,
} from "../transcription/BulkTranscriptionModal";
import TranscriptionStatusCell from "../transcription/TranscriptionStatusCell";
import {
  collectTranscriptionTargets,
  parseDurationSeconds,
} from "../transcription/transcriptionTargets";
import useTranscriptionStatuses, {
  hasTranscriptionPreview,
  TranscriptionCellInfo,
} from "../../hooks/useTranscriptionStatuses";
import Modal from "../modal/Modal";
import IconButton from "../button/IconButton";
import ToolTip from "../tooltip/ToolTip";
import BulkDownloadProgressModal from "./BulkDownloadProgressModal";
import { useBulkDownload } from "../../hooks/useBulkDownload";
import AudioAuditionRow from "../audio-preview/AudioAuditionRow";
import {
  useAudioAudition,
  AudioAudition,
  AuditionItem,
} from "../audio-preview/core/useAudioAudition";

// Props型定義
interface FolderContentsModalProps {
  folder: FolderNode;
  onClose: () => void;
  messageId?: string;
  onFileDownload?: (messageId: string, fileId: string) => void;
}

// Modal の title slot に渡すタイトル要素（フォルダアイコン + 名前）。
// 件数 (N件のファイル) は description slot に分離する。
const FolderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  .material-symbols-rounded {
    font-size: 24px;
    color: var(--soremo-light-gray);
  }
`;

// ヘッダーの「N件のファイル」と一括操作ボタンを横並びにする行。
// Modal の description スロットに渡す。
const HeaderInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

// 一括文字起こし / 一括ダウンロードのアイコンボタン列。
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

// 全ファイル行を密に並べる wrapper。Modal Body の gap: 16px に巻き込まれず、
// 各行は line-height だけで自然に並ぶ。
const FileList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

// フォルダ行：上下 4px の薄い padding でリズムを作り、左端はインデント分のみ。
const FolderRow = styled.div<{ $depth: number; $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
  padding-left: ${(props) => props.$depth * 24}px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--soremo-background);
  }

  .expand-icon {
    font-size: 24px;
    color: var(--soremo-light-gray);
    transition: transform 0.2s ease;
    transform: ${(props) =>
      props.$isExpanded ? "rotate(90deg)" : "rotate(0deg)"};
  }

  .folder-icon {
    font-size: 24px;
    color: var(--soremo-light-gray);
  }
`;

// フォルダ名
const FolderName = styled.span.attrs({
  className: "bodytext-13 u-line-height-100",
})`
  color: var(--soremo-gray);
  flex: 1;
`;

// フォルダ内ファイル数
const FolderFileCount = styled.span.attrs({
  className: "bodytext-11 u-line-height-100",
})`
  color: var(--soremo-light-gray);
`;

// ファイル行：上下 4px の薄い padding。左端はフォルダ行と揃える。
const FileRow = styled.div<{ $depth: number }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
  padding-left: ${(props) => props.$depth * 24}px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--soremo-background);
  }

  .file-icon {
    font-size: 16px;
    color: var(--soremo-light-gray);
  }
`;

// ファイル名
const FileName = styled.span.attrs({
  className: "bodytext-13 u-line-height-100",
})`
  color: var(--soremo-black);
  flex: 1;
  word-break: break-all;
`;

// ダウンロードボタン
const DownloadButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;

  .material-symbols-rounded {
    font-size: 24px;
    color: var(--soremo-light-gray);
  }

  &:hover {
    .material-symbols-rounded {
      color: var(--soremo-blue);
    }
  }
`;

// プレビューボタン
const PreviewButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  flex-shrink: 0;

  .material-symbols-rounded {
    font-size: 20px;
    color: var(--soremo-light-gray);
  }

  &:hover {
    background-color: rgba(0, 154, 206, 0.1);

    .material-symbols-rounded {
      color: var(--soremo-blue);
    }
  }
`;

// ファイル行ラッパー：行高は中身の FileRow / AudioAuditionRow に任せ、
// 既読アバターは absolute で右下に重ねる。これで「既読アバター有り無し」で
// 行の高さがブレない。
const FileRowWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

// ダウンロード既読アバターコンテナ。
// FolderContentsModal の特殊運用として、右端の DropdownMenu (3点リーダー)
// アイコンの「すぐ下」から右揃えで縦に流すレイアウト。
// 行下に少しはみ出すので pointer-events: none でクリックを阻害しない。
const DownloadReceiptsRow = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% - 10px);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  pointer-events: none;
  z-index: 1;

  & > * {
    margin-left: -4px;

    &:first-child {
      margin-left: 0;
    }
  }

  & > * > div {
    box-shadow: 0 0 0 1px #fff;
  }
`;

// サブフォルダコンポーネント
interface FolderTreeItemProps {
  folder: FolderNode;
  expandedFolders: Set<string>;
  onToggle: (folderId: string) => void;
  onPreview: (file: ProjectMessageFile) => void;
  onDownload: (file: ProjectMessageFile) => void;
  audition: AudioAudition;
  /** fileId -> 文字起こしステータス（未実行のファイルは含まれない） */
  transcriptionStatuses: Record<string, TranscriptionCellInfo>;
}

const FolderTreeItem: React.FC<FolderTreeItemProps> = ({
  folder,
  expandedFolders,
  onToggle,
  onPreview,
  onDownload,
  audition,
  transcriptionStatuses,
}) => {
  const isExpanded = expandedFolders.has(folder.id);
  const totalFiles = getTotalFileCount(folder);
  const hasContent = folder.files.length > 0 || folder.children.length > 0;

  return (
    <>
      <FolderRow
        $depth={folder.depth}
        $isExpanded={isExpanded}
        onClick={() => hasContent && onToggle(folder.id)}
      >
        {hasContent ? (
          <span className="material-symbols-rounded expand-icon">
            chevron_right
          </span>
        ) : (
          <span style={{ width: 20 }} />
        )}
        <span className="material-symbols-rounded folder-icon">folder</span>
        <FolderName>{folder.name}</FolderName>
        <FolderFileCount>{totalFiles}件</FolderFileCount>
      </FolderRow>

      {isExpanded && (
        <>
          {/* 子フォルダ */}
          {folder.children.map((child) => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              expandedFolders={expandedFolders}
              onToggle={onToggle}
              onPreview={onPreview}
              onDownload={onDownload}
              audition={audition}
              transcriptionStatuses={transcriptionStatuses}
            />
          ))}

          {/* ファイル */}
          {folder.files.map((file) => (
            <FileRowItem
              key={file.id}
              file={file}
              depth={folder.depth}
              onPreview={onPreview}
              onDownload={onDownload}
              audition={audition}
              transcriptionStatus={transcriptionStatuses[file.id]}
            />
          ))}
        </>
      )}
    </>
  );
};

// ファイル行コンポーネント
interface FileRowItemProps {
  file: ProjectMessageFile;
  depth: number;
  onPreview: (file: ProjectMessageFile) => void;
  onDownload: (file: ProjectMessageFile) => void;
  audition: AudioAudition;
  /** 文字起こしステータス / 本文プレビュー（未実行なら undefined で非表示） */
  transcriptionStatus?: TranscriptionCellInfo;
}

const AudioFileRowItem: React.FC<FileRowItemProps> = ({
  file,
  depth,
  onPreview,
  onDownload,
  audition,
  transcriptionStatus,
}) => {
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const downloadedUsers = file.downloaded_users;

  const handleDetailClick = useCallback(() => {
    setShowDetailModal(true);
  }, []);

  const handleTranscribeClick = useCallback(() => {
    setShowTranscriptionModal(true);
  }, []);

  const audioMenuItems = useMemo(
    () =>
      buildAudioMenuItems({
        onPreview: () => onPreview(file),
        onDetailInfo: handleDetailClick,
        onDownload: () => onDownload(file),
        onTranscribe: handleTranscribeClick,
        acrScanStatus: file.acr_scan_status,
      }),
    [file, onPreview, onDownload, handleDetailClick, handleTranscribeClick],
  );

  return (
    <FileRowWrapper>
      <AudioAuditionRow
        fileName={file.file_name}
        selected={audition.selectedKey === file.id}
        playing={audition.playingKey === file.id}
        rowProps={audition.getRowProps(file.id)}
        onPlayToggle={() => audition.toggle(file.id)}
        menuItems={audioMenuItems}
        acrScanStatus={file.acr_scan_status}
        onAcrClick={handleDetailClick}
        indent={depth * 24}
        nameBadge={<TranscriptionStatusCell entry={transcriptionStatus} />}
        fitFileName={hasTranscriptionPreview(transcriptionStatus)}
      />
      {downloadedUsers && downloadedUsers.length > 0 && (
        <DownloadReceiptsRow>
          {downloadedUsers.map((user) => (
            <UserAvatar
              key={user.id}
              userId={user.id}
              avatar={user.avatar}
              displayName={user.display_name}
              size={12}
            />
          ))}
        </DownloadReceiptsRow>
      )}
      {showDetailModal && (
        <MediaDetailModal
          onClose={() => setShowDetailModal(false)}
          fileName={file.file_name}
          acrScanResult={file.acr_scan_result}
          fileId={file.id}
          acrScanStatus={file.acr_scan_status}
          acrScannedAt={file.acr_scanned_at}
        />
      )}
      {showTranscriptionModal && (
        <MediaTranscriptionModal
          fileId={file.id}
          fileName={file.file_name}
          onClose={() => setShowTranscriptionModal(false)}
          onSeek={(seconds) => {
            // 試聴エンジンはリストで 1 つ共有のため、この行を選択してからシークする
            audition.select(file.id);
            audition.seekTo(seconds);
          }}
        />
      )}
    </FileRowWrapper>
  );
};

const FileRowItem: React.FC<FileRowItemProps> = ({
  file,
  depth,
  onPreview,
  onDownload,
  audition,
  transcriptionStatus,
}) => {
  const isPreviewable = ["image", "video", "audio", "document"].includes(
    file.file_type || "",
  );
  const iconName = getFileIconFromCategoryOrExtension(
    file.file_type,
    file.file_name,
  );
  const downloadedUsers = file.downloaded_users;

  if (file.file_type === "audio") {
    return (
      <AudioFileRowItem
        file={file}
        depth={depth}
        onPreview={onPreview}
        onDownload={onDownload}
        audition={audition}
        transcriptionStatus={transcriptionStatus}
      />
    );
  }

  return (
    <FileRowWrapper>
      <FileRow $depth={depth}>
        <span className="material-symbols-rounded file-icon">{iconName}</span>
        {/* プレビューがあるときは名前を内容幅に留め、直後から本文を続ける */}
        <FileName
          style={
            hasTranscriptionPreview(transcriptionStatus)
              ? { flex: "0 1 auto" }
              : undefined
          }
        >
          {file.file_name}
        </FileName>
        <TranscriptionStatusCell entry={transcriptionStatus} />
        {isPreviewable && (
          <ToolTip description="プレビュー">
            <PreviewButton
              aria-label="プレビュー"
              onClick={(e) => {
                e.stopPropagation();
                onPreview(file);
              }}
            >
              <span className="material-symbols-rounded">visibility</span>
            </PreviewButton>
          </ToolTip>
        )}
        <ToolTip description="ダウンロード">
          <DownloadButton
            aria-label="ダウンロード"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(file);
            }}
          >
            <span className="material-symbols-rounded">download</span>
          </DownloadButton>
        </ToolTip>
      </FileRow>
      {downloadedUsers && downloadedUsers.length > 0 && (
        <DownloadReceiptsRow>
          {downloadedUsers.map((user) => (
            <UserAvatar
              key={user.id}
              userId={user.id}
              avatar={user.avatar}
              displayName={user.display_name}
              size={12}
            />
          ))}
        </DownloadReceiptsRow>
      )}
    </FileRowWrapper>
  );
};

/**
 * フォルダ内容表示モーダル
 */
const FolderContentsModal: React.FC<FolderContentsModalProps> = ({
  folder,
  onClose,
  messageId,
  onFileDownload,
}) => {
  // 展開中のフォルダID
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set([folder.id]),
  );
  // プレビュー中のファイル
  const [previewFile, setPreviewFile] = useState<ProjectMessageFile | null>(
    null,
  );
  // 一括文字起こしモーダル（このフォルダ配下の音声・動画すべてが対象）
  const [showBulkTranscription, setShowBulkTranscription] = useState(false);

  // フォルダ配下（サブフォルダ含む）の音声・動画。一括文字起こしの対象と
  // ステータスチップの購読対象を兼ねる。
  const transcriptionTargets = useMemo<BulkTranscriptionTarget[]>(
    () =>
      collectTranscriptionTargets(flattenFolderToPaths(folder), (entry) => ({
        fileId: entry.file.id,
        fileType: entry.file.file_type,
        durationSeconds: parseDurationSeconds(entry.file.file_metadata),
      })),
    [folder],
  );
  const transcribableFileIds = useMemo(
    () => transcriptionTargets.map((t) => t.fileId),
    [transcriptionTargets],
  );
  const transcriptionStatuses = useTranscriptionStatuses(transcribableFileIds);

  // 表示中（展開されているフォルダ内）のオーディオファイルを表示順に集めて試聴対象にする
  const visibleAudioItems = useMemo<AuditionItem[]>(() => {
    const out: AuditionItem[] = [];
    const pushAudio = (file: ProjectMessageFile) => {
      const src = file.optimized_url || file.original_url;
      if (file.file_type === "audio" && src) {
        out.push({ key: file.id, src });
      }
    };
    // 描画順と同じ: 子フォルダ（再帰）→ 直下ファイル
    const visitFolder = (node: FolderNode) => {
      if (!expandedFolders.has(node.id)) return;
      node.children.forEach(visitFolder);
      node.files.forEach(pushAudio);
    };
    folder.children.forEach(visitFolder);
    folder.files.forEach(pushAudio);
    return out;
  }, [folder, expandedFolders]);

  const audition = useAudioAudition(visibleAudioItems);

  // 取得に成功したファイルの既読登録
  const handleFileDownloaded = useCallback(
    (fileId: string) => {
      if (onFileDownload && messageId) {
        onFileDownload(messageId, fileId);
      }
    },
    [onFileDownload, messageId],
  );

  // 一括ダウンロードの実行と進捗状態（進捗モーダルの表示に使う）
  const {
    downloadState,
    isDownloading: isDownloadingAll,
    startDownload,
    closeDownloadModal,
  } = useBulkDownload(handleFileDownloaded);

  const totalFiles = getTotalFileCount(folder);

  // フォルダの展開/折りたたみ
  const handleToggle = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  // ファイルプレビュー
  const handlePreview = useCallback((file: ProjectMessageFile) => {
    setPreviewFile(file);
  }, []);

  // ファイルダウンロード（直接ダウンロード）+ ダウンロード記録
  // download_url（attachment 付き presigned URL）を優先し、URL 無し/失敗時は
  // トーストで通知する。ダウンロードを開始できたときのみ既読登録する。
  const handleDownload = useCallback(
    async (file: ProjectMessageFile) => {
      const url = resolveDownloadUrl(file);
      if (!url) {
        toast.error(DOWNLOAD_FAILED_MESSAGE);
        return;
      }
      const succeeded = await downloadFile(url, file.file_name);
      if (!succeeded) {
        toast.error(DOWNLOAD_FAILED_MESSAGE);
        return;
      }
      if (onFileDownload && messageId && file.id) {
        onFileDownload(messageId, file.id);
      }
    },
    [onFileDownload, messageId],
  );

  // プレビューを閉じる
  const handleClosePreview = useCallback(() => {
    setPreviewFile(null);
  }, []);

  // フォルダ内の全ファイルを zip でまとめてダウンロード + 既読登録
  const handleDownloadAll = useCallback(() => {
    startDownload(flattenFolderToPaths(folder), folder.name);
  }, [startDownload, folder]);

  return (
    <>
      <Modal
        onClose={onClose}
        size="default"
        titleSize="medium"
        title={
          <FolderTitle>
            <span className="material-symbols-rounded">folder</span>
            <span>{folder.name}</span>
          </FolderTitle>
        }
        description={
          <HeaderInfoRow>
            <span>{totalFiles}件のファイル</span>
            <HeaderActions>
              {transcriptionTargets.length > 0 && (
                <ToolTip description="一括文字起こし" appendTo="parent">
                  <IconButton
                    icon="speech_to_text"
                    onClick={() => setShowBulkTranscription(true)}
                    ariaLabel="一括文字起こし"
                  />
                </ToolTip>
              )}
              {totalFiles > 0 && (
                <ToolTip description="一括ダウンロード" appendTo="parent">
                  <IconButton
                    icon={isDownloadingAll ? "downloading" : "download"}
                    onClick={handleDownloadAll}
                    disabled={isDownloadingAll}
                    ariaLabel="一括ダウンロード"
                  />
                </ToolTip>
              )}
            </HeaderActions>
          </HeaderInfoRow>
        }
        descriptionTone="subtle"
      >
        <FileList>
          {/* 子フォルダ */}
          {folder.children.map((child) => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              expandedFolders={expandedFolders}
              onToggle={handleToggle}
              onPreview={handlePreview}
              onDownload={handleDownload}
              audition={audition}
              transcriptionStatuses={transcriptionStatuses}
            />
          ))}

          {/* 直下のファイル */}
          {folder.files.map((file) => (
            <FileRowItem
              key={file.id}
              file={file}
              depth={0}
              onPreview={handlePreview}
              onDownload={handleDownload}
              audition={audition}
              transcriptionStatus={transcriptionStatuses[file.id]}
            />
          ))}
        </FileList>
      </Modal>

      {/* フォルダ配下の音声・動画をまとめて文字起こし */}
      {showBulkTranscription && (
        <BulkTranscriptionModal
          targets={transcriptionTargets}
          scopeLabel={`フォルダ「${folder.name}」`}
          onClose={() => setShowBulkTranscription(false)}
        />
      )}

      {/* プレビューモーダル（共有コンポーネント使用） */}
      {previewFile && (
        <MediaPreviewModal
          src={previewFile.optimized_url || previewFile.original_url}
          downloadUrl={resolveDownloadUrl(previewFile) ?? undefined}
          mediaType={
            previewFile.file_type === "document"
              ? "pdf"
              : (previewFile.file_type as any)
          }
          onClose={handleClosePreview}
          fileName={previewFile.file_name}
          waveformUrl={
            previewFile.has_waveform && previewFile.waveform_url
              ? previewFile.waveform_url
              : undefined
          }
        />
      )}

      {/* 一括ダウンロードの進捗・結果モーダル */}
      {downloadState && (
        <BulkDownloadProgressModal
          state={downloadState}
          downloadName={folder.name}
          onClose={closeDownloadModal}
        />
      )}
    </>
  );
};

export default FolderContentsModal;
